import React, { useEffect, useMemo, useState } from "react";
import {
  NavLink,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import queryString from "query-string";
import {
  Button,
  InlineLoading,
  InfoBar,
  MainNavigation,
  MainNavigationItem,
  SecondaryNavigation,
  SecondaryNavigationTitle,
  Tab,
  Tabs,
  User,
} from "@wfp/ui";

import { getCurrentUser } from "data-handler/ducks/auth";
import { countriesDuck } from "data-handler/ducks/countries";
import {
  getSelectedCountry,
  setSelectedCountry,
} from "data-handler/ducks/countryProfile";
import Dashboard from "components/Analytics/Dashboard";
import RawDataExtractPage from "components/Analytics/rawDataExtractPage";
import SchoolSelect from "components/SchoolSelect";
import CountrySelectModal from "components/CountryAdmin/CountryAdminContent/CountryAdminProfile/CountrySelectModal";
import UserNavigation from "components/UserNavigation";
import TabLink from "components/TabLink";
import {
  getIsAdmin,
  getIsCountryAdmin,
  getIsSchoolGroupAdmin,
  getIsViewer,
} from "helpers/users";

import "components/CountryAdmin/CountryAdminHeader/MainNav/_content.scss";
import "components/CountryAdmin/CountryAdminContent/_content.scss";

const Analytics: React.ElementType = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const urlParams = queryString.parse(location.search);
  const { path } = useRouteMatch();
  const basePath = path.endsWith("/") ? path.slice(0, -1) : path;
  const currentUser = useSelector(getCurrentUser);
  const selectedCountry = useSelector(getSelectedCountry);
  const countries = useSelector(countriesDuck.getList);
  const countriesIsFetching = useSelector(countriesDuck.isFetching());
  const isViewer =
    currentUser?.is_external_viewer || currentUser?.is_wfp_viewer;
  const requestedCountryId = useMemo(() => {
    const countryId = urlParams.country_id;
    const parsed = Number(
      Array.isArray(countryId) ? countryId[0] || "" : countryId || ""
    );

    return Number.isNaN(parsed) ? null : parsed;
  }, [urlParams.country_id]);
  const userCountries = useMemo(
    () =>
      getIsCountryAdmin(currentUser) ||
      getIsSchoolGroupAdmin(currentUser) ||
      getIsViewer(currentUser)
        ? currentUser?.countries ||
          (currentUser?.country ? [currentUser.country] : [])
        : [],
    [currentUser]
  );
  const [countrySelectModalOpen, setCountrySelectModalOpen] = useState(
    currentUser?.user_role === "global_administrator" && !selectedCountry
      ? true
      : false
  );

  useEffect(() => {
    if (
      !requestedCountryId &&
      userCountries.length === 1 &&
      selectedCountry?.id !== userCountries[0]?.id
    ) {
      dispatch(setSelectedCountry(userCountries[0]));
    }
  }, [
    currentUser,
    dispatch,
    requestedCountryId,
    selectedCountry,
    userCountries,
  ]);

  useEffect(() => {
    if (!requestedCountryId || selectedCountry?.id === requestedCountryId) {
      return;
    }

    const scopedCountry = userCountries.find(
      (country) => country?.id === requestedCountryId
    );
    if (scopedCountry) {
      dispatch(setSelectedCountry(scopedCountry));
      return;
    }

    if (userCountries.length > 0) {
      return;
    }

    const globalCountry = countries.find(
      (country) => country?.id === requestedCountryId
    );
    if (globalCountry) {
      dispatch(setSelectedCountry(globalCountry));
      return;
    }

    if (
      userCountries.length === 0 &&
      !countriesIsFetching &&
      countries.length === 0
    ) {
      dispatch(countriesDuck.fetchList());
    }
  }, [
    countries,
    countriesIsFetching,
    currentUser,
    dispatch,
    requestedCountryId,
    selectedCountry,
    userCountries,
  ]);

  const tabs = [
    {
      label: (
        <FormattedMessage id="analytics.dashboard" defaultMessage="Dashboard" />
      ),
      href: `${basePath}/Dashboard`,
    },
    {
      label: (
        <FormattedMessage
          id="CountryAdmin.RawDataExtractModal"
          defaultMessage="Raw Data Extract"
        />
      ),
      href: `${basePath}/DataDownload`,
    },
  ];

  const setInitTabAndRedirect = () => {
    const index = tabs.findIndex((tab) => tab.href === location.pathname);
    if (index < 0) {
      history.replace({
        pathname: tabs[0].href,
        search: location.search,
      });
    }
    return index >= 0 ? index : 0;
  };

  const tabIndex = setInitTabAndRedirect();

  const loadTab = (index: number) => {
    const tab = tabs[index];
    if (tab) {
      history.push({
        pathname: tab.href,
        search: location.search,
      });
    }
  };

  const tabsKey = tabs.reduce((acc, cur) => acc + cur.label, "");

  const submitSelectedCountry = (country) => {
    const nextParams = {
      ...queryString.parse(location.search),
      country_id: country?.id ? String(country.id) : undefined,
    };

    [
      "adm_1",
      "adm_2",
      "school_id",
      "school_year",
      "year_month",
      "year",
      "month",
    ].forEach((key) => {
      delete nextParams[key];
    });

    history.replace({
      pathname: location.pathname,
      search: queryString.stringify(nextParams),
    });
  };

  return (
    <div className="content" style={{ flex: 1, width: "100%" }}>
      {urlParams.select === "school" && <SchoolSelect />}
      {process.env.REACT_APP_ENV && process.env.REACT_APP_ENV !== "prod" && (
        <div className={`environment-indicator ${process.env.REACT_APP_ENV}`}>
          {process.env.REACT_APP_ENV}
        </div>
      )}

      <MainNavigation
        className="content__header"
        pageWidth="full"
        logo={
          !currentUser ? null : (
            <NavLink
              exact
              to={{
                search: queryString.stringify({
                  ...urlParams,
                  select: "school",
                }),
              }}
              renderListElement={TabLink}
            >
              <Button className="country-header">
                {currentUser.is_admin ? (
                  <FormattedMessage
                    id="CountryAdmin.administrator"
                    defaultMessage="Administrator"
                  />
                ) : !currentUser.country.name ? (
                  <InlineLoading />
                ) : (
                  <> {currentUser.country.name}</>
                )}
              </Button>
            </NavLink>
          )
        }
      >
        {() => (
          <>
            <MainNavigationItem
              className="wfp--main-navigation__user"
              subNavigation={<UserNavigation />}
            >
              <User
                ellipsis
                name={currentUser ? currentUser.last_name : "no name"}
                missingImage="letter"
              />
            </MainNavigationItem>
          </>
        )}
      </MainNavigation>

      {isViewer ? (
        <InfoBar>
          <FormattedMessage
            id="menu.WFPReadOnlyMessage"
            defaultMessage="You are logged in read-only mode. To change it, please contact your administrator."
          />
        </InfoBar>
      ) : null}

      <div className="country-admin-inner-content">
        <div className="country-admin-main-content country-profile-page raw-data-extract-page">
          <SecondaryNavigation pageWidth="lg">
            <SecondaryNavigationTitle>
              <FormattedMessage
                id="schoolSelect.analytics"
                defaultMessage="Analytics"
              />
              <Button
                className="wfp--btn--secondary"
                disabled={!getIsAdmin(currentUser) && userCountries.length <= 1}
                onClick={() => {
                  setCountrySelectModalOpen(true);
                }}
              >
                {selectedCountry ? (
                  selectedCountry.name
                ) : (
                  <FormattedMessage
                    id="CountryAdmin.noCountrySelected"
                    defaultMessage="No country selected"
                  />
                )}
              </Button>
            </SecondaryNavigationTitle>
            <Tabs key={tabsKey} onSelectionChange={loadTab} selected={tabIndex}>
              {tabs.map((tab) => (
                <Tab key={tab.href} label={tab.label} />
              ))}
            </Tabs>
          </SecondaryNavigation>
          <CountrySelectModal
            countrySelectModalOpen={countrySelectModalOpen}
            setCountrySelectModalOpen={setCountrySelectModalOpen}
            country={selectedCountry}
            submitSelectedCountry={submitSelectedCountry}
            availableCountries={
              getIsAdmin(currentUser) ? undefined : userCountries
            }
          />
          <Switch>
            <Route exact path={`${basePath}/Dashboard`}>
              <Dashboard />
            </Route>
            <Route
              exact
              path={`${basePath}/DataDownload`}
              component={RawDataExtractPage}
            />
            <Route exact path={basePath}>
              {}
              <RawDataExtractPage />
            </Route>
          </Switch>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
